import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, finalize, map} from 'rxjs/operators';

import {PAGE, WORD_API_URL, WORD_DETAIL_API_URL, WORD_LINKS_API_URL} from '../../defines/protocol';
import {SimpleWord} from '../../models/simple-word.model';
import {Word} from '../../models/word.model';
import {ListLoader} from '../../interfaces/list-loader';
import {ApiService} from '../api/api.service';
import {SearchParam} from '../../defines/client.const';
import {NormalizeSearchKeyword} from '../../defines/client.func';


@Injectable({
  providedIn: 'root'
})
export class WordService implements ListLoader<SimpleWord> {
  protected urlApi = WORD_API_URL;

  protected totalCount: number | null = null;
  protected listWord: Array<SimpleWord> | null = null;
  private mapWord: Map<string, SimpleWord> | null = null;
  private nextUrl: string | null = null;
  private load$: Observable<boolean> | null = null;
  private loadDetail$: Observable<Array<Word>> | null = null;


  constructor(
    protected api: ApiService,
  ) {
  }

  /******************************************************************************
   * ListLoader<SimpleWord> 구현부
   *****************************************************************************/
  public getList(): Array<SimpleWord> | null {
    return this.listWord;
  }
  public getCurCount(): number | null {
    // @ts-ignore
    return this.listWord?.length ?? null;
  }
  public getTotalCount(): number | null {
    return this.totalCount;
  }
  public IsComplete(): boolean {
    return this.totalCount === 0 ||
      (this.totalCount !== null && this.listWord?.length === this.totalCount);
  }
  public IsLoading(): boolean {
    return this.load$ !== null;
  }
  public getById(id: string): SimpleWord | null {
    return this.mapWord?.get(id) ?? null;
  }

  public ResetList(param?: SearchParam): void {
    this.nextUrl = null;
    this.listWord = null;
    this.mapWord = null;
    this.totalCount = null;

    if (param) {
      const searchParams = new URLSearchParams();

      // 우선순위가 있는 파라미터 처리
      if ('index' in param) {
        searchParams.set('index', param.index!);
      } else if ('keyword' in param) {
        const keyword = NormalizeSearchKeyword(param.keyword);
        if (keyword) {
          searchParams.set('search', keyword);
        }
      } else if ('hanja' in param) {
        searchParams.set('hanja', param.hanja!);
        // 음(hangul)은 한자 검색일 때만 함께 전달한다.
        if (param.hangul) {
          searchParams.set('hangul', param.hangul);
        }
      }

      // 나머지 선택적 파라미터 처리
      if ('level' in param) {
        searchParams.set('level', param.level!.toString());
      }
      if ('start' in param) {
        searchParams.set('start', param.start!);
      }
      if ('saja' in param) {
        searchParams.set('saja', param.saja!.toString());
      }

      const queryString = searchParams.toString();
      this.nextUrl = queryString ? `${this.urlApi}?${queryString}` : this.urlApi;
    } else {
      this.nextUrl = this.urlApi;
    }
  }

  /**
   * return true if first page
   */
  public GetNextList(): Observable<boolean> {
    if (this.nextUrl === null) {
      return throwError({error: 'No more pages in WordList'});
    }

    if (this.load$) {
      return this.load$;
    }

    if (this.nextUrl === null) {
      throw new Error('WordService::GetNextList() nextUrl is null');
    }
    return this.load$ = this.api.Get(this.nextUrl).pipe(
      catchError(err => {
        if ('status' in err && err.status === 404) {
          this.totalCount = 0;
        }
        throw err;
      }),
      finalize(() => {
        this.load$ = null;
      } ),
      map((data: PAGE) => {
          console.log('getWordList.api.Get data =', data);

          let first = false;
          if (this.totalCount === null) {
            this.totalCount = data.count;
            this.listWord = new Array<SimpleWord>();
            this.mapWord = new Map<string, SimpleWord>();
            first = true;
          }
          else if (this.totalCount !== data.count) {
            console.error('[error]' + this.urlApi, data );
          }

          data.results.forEach(value => {
            if (this.listWord === null || this.mapWord === null) {
              throw new Error('WordService::GetNextList() listWord or mapWord is null');
            }
            const word = new SimpleWord(value);
            this.listWord.push(word);
            this.mapWord.set(word.id, word);
          });

          this.nextUrl = data.next;

          return first;
        })
    );
  }


  /******************************************************************************
   * 상세 단어어 정보 획득
   * 위쪽의 목록에는 간섭이 없도록 처리하는 것이 좋다.
   * TODO: 로딩중인 것을 처리. (예외상황 처리 추가해야 함)
   *****************************************************************************/
  // tslint:disable-next-line:variable-name
  public GetDetail(word_id: string): Observable<Word> {
    const url = WORD_DETAIL_API_URL + word_id + '/';
    console.log('WordService::GetDetail() url =', url);
    return this.api.Get(url).pipe(
      map(value => {
        const out = new Word(value);
        console.log('WordService::GetDetail data = ', value, out);
        return out;
      })
    );
  }


  /******************************************************************************
   * 링크용 단순 단어어 정보 획득
   * 위쪽의 목록에는 간섭이 없도록 처리하는 것이 좋다.
   * TODO: 로딩중인 것을 처리. (예외상황 처리 추가해야 함)
   *****************************************************************************/
  // tslint:disable-next-line:variable-name
  public GetLinkInfo(word_id: string): Observable<Array<Word>> {
    const url = WORD_LINKS_API_URL + word_id + '/';
    console.log('WordService::GetLinkInfo() url =', url);

    return this.loadDetail$ = this.api.Get(url).pipe(
      finalize(() => this.loadDetail$ = null),
      map(value => {
        const out = new Array<Word>();
        value.forEach((v: any) => out.push(new Word(v)));
        console.log('WordService::GetLinkInfo data = ', value, out);
        return out;
      })
    );
  }


}
